'use client'

import { useRef } from "react"
import ProductCard, { CARD_ASPECT } from "../product/ProductCard"
import { Loader2, PackageOpen } from "lucide-react"
import { useInfiniteProducts } from "@/hooks/use-infinite-products"
import { useInfiniteScroll } from "@/hooks/use-infinite-scroll"
import Skeleton from "@/components/elements/Skeleton"
import EmptyState from "../elements/EmptyState"

const gridClass = 'grid grid-cols-2 lg:grid-cols-4 gap-x-1 gap-y-8 lg:gap-y-12'

export default function AllProducts({ vendor, title, description, session }: { vendor: any, wishlist?: any, title?: string, description?: string, session?: any }) {
  const loadMoreRef = useRef<HTMLDivElement>(null)

  const { products, isLoading, isLoadingMore, metadata, loadMore, hasMore, pendingCount } = useInfiniteProducts({
    vendorId: vendor?.id,
    limit: 20,
    initialFilters: {},
    session
  })

  useInfiniteScroll({
    targetRef: loadMoreRef,
    onLoadMore: loadMore,
    enabled: hasMore && !isLoading && !isLoadingMore,
  })


  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <section className="w-full px-4 lg:px-6 py-12 lg:py-16">
      <div className="flex items-end justify-between gap-6 mb-6 lg:mb-8">
        <div className="min-w-0">
          <h2 className="text-2xl sm:text-4xl tracking-tight text-foreground">{title || 'Бүх бүтээгдэхүүн'}</h2>
          {description && <p className="mt-2 text-sm text-muted-foreground">{description}</p>}
        </div>
        {!isLoading && <span className="shrink-0 text-[13px] text-muted-foreground tabular-nums">{metadata.total} бүтээгдэхүүн</span>}
      </div>
      {isLoading ? (
        <div className={gridClass}>
          {Array.from({ length: 8 }).map((_, index) => (
            <SkeletonProduct key={index} aspectRatioClass={CARD_ASPECT} />
          ))}
        </div>
      ) : products.length > 0 ? (
        <>
          <div className={gridClass}>
            {products.map((product: any) => (
              <ProductCard key={product.customId || product.id} product={product} vendor={vendor} />
            ))}
            {Array.from({ length: pendingCount }).map((_, i) => (
              <div key={`pending-${i}`} ref={i === 0 ? loadMoreRef : undefined}>
                <SkeletonProduct aspectRatioClass={CARD_ASPECT} animate={false} />
              </div>
            ))}
          </div>
          <div ref={pendingCount === 0 ? loadMoreRef : null} className="w-full pt-12 flex justify-center">
            {isLoadingMore && (
              <div className="flex items-center gap-2 text-muted-foreground text-[13px]">
                <Loader2 className="size-4 animate-spin" strokeWidth={1.5} />
                <span>Ачаалж байна...</span>
              </div>
            )}
            {!hasMore && products.length > 0 && (
              <div className="flex flex-col items-center gap-4 text-[13px]">
                <span className="text-muted-foreground">Бүх бүтээгдэхүүн ачаалагдлаа ({metadata.total})</span>
                <button type="button" onClick={scrollToTop} className="h-10 px-6 border border-foreground text-foreground tracking-wide transition-colors duration-150 hover:bg-foreground hover:text-background focus-visible:outline-1 focus-visible:outline-offset-4 focus-visible:outline-foreground">Дээш буцах</button>
              </div>
            )}
          </div>
        </>
      ) : (
        <EmptyState icon={PackageOpen} title="Бүтээгдэхүүн байхгүй байна" description="Яг одоогоор ямар нэгэн бүтээгдэхүүн байхгүй байна. Хэсэг хугацааны дараа дахин шалгана уу." />
      )}
    </section>
  )
}

function SkeletonProduct({ aspectRatioClass, animate = true }: { aspectRatioClass?: string, animate?: boolean }) {
  const still = animate ? '' : 'animate-none'
  return (
    <div>
      <div className={`${aspectRatioClass || 'aspect-square'} w-full relative overflow-hidden`}>
        <Skeleton className={`absolute inset-0 w-full h-full rounded-none ${still}`} />
      </div>
      <Skeleton className={`h-3.5 w-3/4 mt-3 rounded-none ${still}`} />
      <Skeleton className={`h-3.5 w-1/3 mt-2 rounded-none ${still}`} />
    </div>
  )
}
